import { Add, Delete, Edit } from '@mui/icons-material'
import {
	Box,
	Button,
	DialogActions,
	DialogContent,
	DialogTitle,
	Divider,
	Fade,
	IconButton,
	Paper,
	Table,
	TableBody,
	TableCell,
	TableContainer,
	TableHead,
	TableRow,
	Typography,
} from '@mui/material'
import { action, runInAction } from 'mobx'
import { observer, useLocalObservable } from 'mobx-react'
import { useEffect, useMemo } from 'react'
import {
	BaseTandemFieldInstanceModel,
	TandemFieldConfiguration,
	TandemFieldInstance,
} from '../../api/clients/tandem/DTOs'
import TandemFieldInstancesClient from '../../api/clients/tandem/TandemFieldInstancesClient'
import TandemFieldsClient from '../../api/clients/tandem/TandemFieldsClient'
import {
	FormBuilderViewer,
	StandaloneCellManager,
} from '../../modules/FormBuilderInterop/Runtimes/Standalone'
import { useModals } from '../../services/notifications/ModalService'
import { usePrompts } from '../../services/notifications/PromptService'
import { toastService } from '../../services/notifications/ToastService'
import AdministrationPageContainer from '../Organization/Administration/AdministrationPageContainer'
import { AdministrationPageTabs } from '../Organization/Administration/AdministrationPageTabs'
import { AddTandemFieldDialog } from './AddTandemFieldDialog'

type TandemFieldsSectionProps = {
	accountId: number
	tabLabels: string[]
	onTabChanged: (event: React.SyntheticEvent, value: number) => void
}

export const TandemFieldsSection = observer(
	(props: TandemFieldsSectionProps) => {
		const modalService = useModals()
		const promptService = usePrompts()

		const localStore = useLocalObservable(() => ({
			fieldInstances: [] as TandemFieldInstance[],
			fields: [] as TandemFieldConfiguration[],
		}))

		const fieldsClient = new TandemFieldsClient()
		const fieldInstancesClient = new TandemFieldInstancesClient()

		useEffect(() => {
			fetchFields()
			fetchFieldInstances()
		}, [])

		const fetchFields = async () => {
			try {
				const { data: fields } = await fieldsClient.getFields()

				runInAction(() => {
					localStore.fields = fields
				})
			} catch (e) {
				console.error(e)

				toastService.displayToast({
					message: 'Failed to fetch fields',
					area: 'global',
					severity: 'error',
				})
			}
		}

		const fetchFieldInstances = async () => {
			try {
				const { data: fieldInstances } =
					await fieldInstancesClient.getFieldInstances(props.accountId)

				runInAction(() => {
					localStore.fieldInstances = fieldInstances
				})
			} catch (e) {
				console.error(e)

				toastService.displayToast({
					message: 'Failed to fetch field instances',
					area: 'global',
					severity: 'error',
				})
			}
		}

		const handleEditField = (field: TandemFieldInstance) => {
			const fieldConfiguration = localStore.fields.find(
				(v) => v.fieldName === field.fieldName,
			)

			if (fieldConfiguration === undefined)
				throw new Error(
					`could not find field configuration with name ${field.fieldName}`,
				)

			modalService
				.showForm((props) => (
					<EditFieldDialog
						field={field}
						fieldConfiguration={fieldConfiguration}
						onConfirm={(v) => props.close({ closeResult: 'okay', value: v })}
						onCancel={() => props.close({ closeResult: 'cancel' })}
					/>
				))
				.then(
					action((v) => {
						if (v.closeResult === 'cancel') return

						const fieldValue = v.value as BaseTandemFieldInstanceModel

						fieldInstancesClient
							.updateFieldInstance(props.accountId, field.id, fieldValue)
							.then(
								action((response) => {
									const innerInstances = [...localStore.fieldInstances]
									const index = localStore.fieldInstances.indexOf(field)
									if (index < 0) throw new Error('failed to find field in list')

									innerInstances[index] = {
										...field,
										properties: response.data.properties,
									}

									localStore.fieldInstances = innerInstances
								}),
							)

						toastService.displayToast({
							message: 'Successfully updated field',
							area: 'global',
							severity: 'success',
						})
					}),
				)
				.catch((e) => {
					console.error(e)

					toastService.displayToast({
						message: 'Error updating field',
						area: 'global',
						severity: 'error',
					})
				})
		}

		const handleDeleteField = async (id: number) => {
			try {
				await fieldInstancesClient.deleteFieldInstance(props.accountId, id)

				runInAction(() => {
					localStore.fieldInstances = localStore.fieldInstances.filter(
						(v) => v.id !== id,
					)
				})
				toastService.displayToast({
					message: 'Successfully deleted field',
					area: 'global',
					severity: 'success',
				})
			} catch (e) {
				console.error(e)

				toastService.displayToast({
					message: 'Error deleting field',
					area: 'global',
					severity: 'error',
				})
			}
		}

		const handleAddField = async (field: BaseTandemFieldInstanceModel) => {
			try {
				const { data: fieldInstance } =
					await fieldInstancesClient.createFieldInstance(props.accountId, field)

				toastService.displayToast({
					message: 'Field instance created successfully',
					area: 'global',
					severity: 'success',
				})

				runInAction(() => {
					localStore.fieldInstances = [
						...localStore.fieldInstances,
						fieldInstance,
					]
				})
			} catch (e) {
				console.error(e)

				toastService.displayToast({
					message: 'Failed to create field instance',
					area: 'global',
					severity: 'error',
				})
			}
		}

		return (
			<AdministrationPageContainer
				title="Tandem Account Fields"
				actions={
					<div>
						<Button
							variant="contained"
							startIcon={<Add />}
							onClick={() => {
								modalService
									.showForm((props) => (
										<AddTandemFieldDialog
											availableFields={localStore.fields.filter(
												(v) =>
													!localStore.fieldInstances
														.map((v) => v.fieldName)
														.includes(v.fieldName),
											)}
											onConfirm={(v) =>
												props.close({ closeResult: 'okay', value: v })
											}
											onCancel={() => props.close({ closeResult: 'cancel' })}
										/>
									))
									.then((result) => {
										if (result.closeResult === 'okay')
											handleAddField(
												result.value as BaseTandemFieldInstanceModel,
											)
									})
							}}
						>
							Add Field
						</Button>
					</div>
				}
			>
				<Box display="flex" flexDirection="column" width="100%" height="100%">
					<AdministrationPageTabs
						currentTab={1}
						onTabChanged={props.onTabChanged}
						tabLabels={props.tabLabels}
					/>
					<Divider />
					<Fade in>
						<Box
							display="flex"
							flexDirection="column"
							height="100%"
							width="100%"
							marginY={2}
							paddingY={1}
							paddingX={1}
							gap={1}
						>
							<TableContainer component={Paper}>
								<Table>
									<TableHead>
										<TableRow>
											<TableCell>Name</TableCell>
											<TableCell>Threshold</TableCell>
											<TableCell align="right">Actions</TableCell>
										</TableRow>
									</TableHead>
									<TableBody>
										{localStore.fieldInstances.map((field) => (
											<TableRow key={field.id}>
												<TableCell>{field.fieldName}</TableCell>
												<TableCell>{field.properties.threshold}</TableCell>
												<TableCell align="right">
													<IconButton
														size="small"
														onClick={() => handleEditField(field)}
													>
														<Edit />
													</IconButton>
													<IconButton
														size="small"
														onClick={() => {
															promptService
																.showDialog((props) => (
																	<ConfirmDeleteInstancePrompt
																		fieldName={
																			localStore.fields.find(
																				(v) => v.fieldName === field.fieldName,
																			)?.displayName ?? field.fieldName
																		}
																		onCancel={() => props.close('cancel')}
																		onConfirm={() => props.close('okay')}
																	/>
																))
																.then((v) => {
																	if (v === 'okay') handleDeleteField(field.id)
																})
														}}
													>
														<Delete />
													</IconButton>
												</TableCell>
											</TableRow>
										))}
									</TableBody>
								</Table>
							</TableContainer>
						</Box>
					</Fade>
				</Box>
			</AdministrationPageContainer>
		)
	},
)

type EditFieldDialogProps = {
	field: TandemFieldInstance
	fieldConfiguration: TandemFieldConfiguration
	onConfirm: (updatedField: BaseTandemFieldInstanceModel) => void
	onCancel: () => void
}

const EditFieldDialog = observer((props: EditFieldDialogProps) => {
	const localStore = useLocalObservable(() => ({
		fieldProperties: { ...props.field.properties },
	}))

	const cellManager = useMemo(() => {
		return new StandaloneCellManager(
			props.fieldConfiguration.formBuilderSchema,
			localStore.fieldProperties,
		)
	}, [localStore.fieldProperties])

	return (
		<>
			<DialogTitle>{props.fieldConfiguration.displayName}</DialogTitle>
			<DialogContent>
				{cellManager !== undefined && (
					<FormBuilderViewer cellManager={cellManager} />
				)}
			</DialogContent>
			<DialogActions>
				<Button onClick={props.onCancel}>Cancel</Button>
				<Button
					variant="contained"
					onClick={() =>
						props.onConfirm({
							fieldName: props.field.fieldName,
							properties: localStore.fieldProperties,
						})
					}
					disabled={
						JSON.stringify(localStore.fieldProperties) ===
						JSON.stringify(props.field.properties)
					}
				>
					Update Field
				</Button>
			</DialogActions>
		</>
	)
})

type ConfirmDeleteInstancePromptProps = {
	fieldName: string
	onConfirm: () => void
	onCancel: () => void
}

const ConfirmDeleteInstancePrompt = (
	props: ConfirmDeleteInstancePromptProps,
) => {
	return (
		<>
			<DialogTitle>Confirm Delete Field</DialogTitle>
			<DialogContent>
				<Typography>
					Are you sure that you want to remove the <b>{props.fieldName}</b>{' '}
					field?
				</Typography>
			</DialogContent>
			<DialogActions>
				<Button color="primary" onClick={props.onCancel}>
					Cancel
				</Button>
				<Button color="primary" variant="contained" onClick={props.onConfirm}>
					Confirm
				</Button>
			</DialogActions>
		</>
	)
}
